import "date-fns";
import React, { forwardRef } from "react";
//#region types
import { MonthPickerProps, MonthPickerState, MonthPickerHandle } from "models/propTypes/datepickerPropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import DatePicker from "react-datepicker";
import Icon from "../icon";
import { FormLabel, Grid, Box } from "@mui/material";
//#endregion
import "react-datepicker/dist/react-datepicker.css";
import "theme/custom/react-datepicker.css";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { CSSHelper, DateHelper } from "utils/helpers";

const MonthPicker: React.ForwardRefRenderFunction<MonthPickerHandle, MonthPickerProps> = (props, ref) => {
  let datePicker: DatePicker;

  const [state, setState] = React.useState<MonthPickerState>({
    isCalenderOpen: false,
  });

  React.useImperativeHandle(ref, () => ({
    setFocus: () => {
      datePicker.setFocus();
    },
  }));

  const onDateClick = () => {
    datePicker.setFocus();
  };

  const handleCalendarOpen = () => {
    setState({
      ...state,
      isCalenderOpen: true,
    });
  };

  const handleCalendarClose = () => {
    setState({
      ...state,
      isCalenderOpen: false,
    });
  };

  const onChange = (date: Date | null, event: React.SyntheticEvent<any, Event> | undefined) => {
    event?.persist();
    if (props.setLastDayOfMonth) {
      date = date ? DateHelper.getLastDayOfMonth(date) : date;
    } else {
      date?.setDate(1);
    }
    date?.setHours(0, 0, 0, 0);
    props.onChange(date ?? undefined, event);
    if (date === null) {
      props.onBlur && props.onBlur({ target: { name: props.name } } as React.FocusEvent<HTMLInputElement>);
    }
  };

  const popperClass = CSSHelper.getDatePickerPopperClassByDisplayType(props.displayType);

  return (
    <React.Fragment>
      <Box
        className={clsx(
          cssExports.datePickerInputOuter,
          state.isCalenderOpen ? cssExports.datePickerOpen : "",
          props.error ? cssExports.error : "",
          props.disabled ? cssExports.disabledPicker : ""
        )}
      >
        <Grid container item direction="row" alignItems="center" wrap="nowrap">
          <Grid item className={cssExports.datePickerInputIcon} onClick={onDateClick}>
            <Icon icon={IconDefinitionConstants.WMonthCalendar} />
          </Grid>
          <Grid item xs className={cssExports.datePickerInput}>
            <Grid container item direction="column">
              <FormLabel onClick={onDateClick} className={cssExports.shrinkedLabel}>
                {props.label}
              </FormLabel>
              <DatePicker
                {...props}
                ref={(component: DatePicker) => (datePicker = component)}
                selected={props.selected && new Date(props.selected)}
                startDate={props.startDate && new Date(props.startDate)}
                endDate={props.endDate && new Date(props.endDate)}
                maxDate={props.maxDate && new Date(props.maxDate)}
                minDate={props.minDate && new Date(props.minDate)}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                onCalendarClose={handleCalendarClose}
                onCalendarOpen={handleCalendarOpen}
                onChange={onChange}
                isClearable={!props.hideClearable}
                wrapperClassName={cssExports.monthPicker}
                popperClassName={`${popperClass}`}
                onInputClick={onDateClick}
                disabledKeyboardNavigation
                popperModifiers={[
                  {
                    name: "flip",
                    options: {
                      fallbackPlacements: ["auto"],
                    },
                  },
                  ...(props.popperModifiers ?? []),
                ]}
              />
            </Grid>
          </Grid>
          {props.success ? (
            <Grid item className={clsx(cssExports.datePickerInputIcon, cssExports.success)}>
              <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
      </Box>
    </React.Fragment>
  );
};
export default forwardRef(MonthPicker);
